import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Check, X } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

const STORAGE_KEY = "g21-cookie-consent";

const setPostHogConsent = (accepted) => {
    // PostHog is loaded via index.html; opt-out blocks all event capture.
    if (typeof window === "undefined" || !window.posthog) return;
    try {
        if (accepted) {
            window.posthog.opt_in_capturing();
        } else {
            window.posthog.opt_out_capturing();
        }
    } catch {
        /* ignore */
    }
};

/**
 * Cookie consent banner – DSGVO/TTDSG compliant minimal banner.
 * Two actions: "Alle akzeptieren" (incl. PostHog analytics) or "Nur notwendige"
 * (essential cookies only, opts PostHog out).
 *
 * The choice is stored in localStorage. The banner is hidden once a choice exists.
 * To re-open it, see <CookieSettingsButton /> below.
 */
export const CookieBanner = () => {
    const t = useT();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) {
                // Default to opt-out until user explicitly accepts
                setPostHogConsent(false);
                setOpen(true);
            } else if (stored === "accepted") {
                setPostHogConsent(true);
            } else {
                setPostHogConsent(false);
            }
        } catch {
            setOpen(true);
        }

        const handler = () => setOpen(true);
        window.addEventListener("g21:open-cookie-banner", handler);
        return () => window.removeEventListener("g21:open-cookie-banner", handler);
    }, []);

    const decide = (choice) => {
        try {
            localStorage.setItem(STORAGE_KEY, choice);
        } catch {
            /* private browsing – just hide */
        }
        setPostHogConsent(choice === "accepted");
        setOpen(false);
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[80] p-3 sm:p-5 pointer-events-none"
            data-testid="cookie-banner"
            role="dialog"
            aria-label="Cookie-Einstellungen"
        >
            <div className="container-page pointer-events-auto">
                <div className="bg-ink text-white shadow-2xl border-l-2 border-brand-500 max-w-md ml-auto p-4 sm:p-5">
                    <div className="flex items-start gap-2.5">
                        <Cookie size={16} className="text-clay-300 shrink-0 mt-0.5" />
                        <p className="font-body text-xs text-white/80 leading-relaxed flex-1">
                            {t({
                                de: "Wir verwenden technisch notwendige Cookies. Mit Ihrer Einwilligung zusätzlich anonyme Statistik-Cookies. Mehr in der ",
                                en: "We use technically necessary cookies. With your consent, also anonymous statistics cookies. More in the ",
                            })}
                            <Link
                                to="/datenschutz"
                                className="underline decoration-clay-300/60 underline-offset-2 hover:decoration-clay-300"
                                data-testid="cookie-link-datenschutz"
                            >
                                {t({ de: "Datenschutzerklärung", en: "privacy policy" })}
                            </Link>
                            .
                        </p>
                    </div>

                    <div className="flex gap-2 mt-3">
                        <button
                            onClick={() => decide("essential")}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border border-white/25 text-white/85 hover:bg-white/5 hover:border-white text-xs font-body font-medium tracking-wide transition-colors"
                            data-testid="cookie-essential"
                        >
                            <X size={12} />
                            {t({ de: "Nur notwendige", en: "Essential only" })}
                        </button>
                        <button
                            onClick={() => decide("accepted")}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-xs font-body font-medium tracking-wide transition-colors"
                            data-testid="cookie-accept"
                        >
                            <Check size={12} />
                            {t({ de: "Akzeptieren", en: "Accept" })}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Re-opens the banner so users can change their mind. Render in the footer.
 */
export const CookieSettingsButton = ({ className = "" }) => {
    const t = useT();
    return (
        <button
            type="button"
            onClick={() => {
                try {
                    localStorage.removeItem(STORAGE_KEY);
                } catch {
                    /* ignore */
                }
                window.dispatchEvent(new Event("g21:open-cookie-banner"));
            }}
            className={`inline-flex items-center gap-2 hover:text-ink transition-colors ${className}`}
            data-testid="cookie-settings-button"
        >
            <Cookie size={12} />
            {t({ de: "Cookie-Einstellungen", en: "Cookie settings" })}
        </button>
    );
};
